/**
 * Gemeinsame Vertraege zwischen Web-Client und API.
 * Beide Seiten importieren aus dieser Datei, damit das WS-Protokoll
 * nicht auseinanderlaeuft.
 */

export const WS_PATH = '/ws';

/* ---------------------------------------------------------------- Ticker */

// Der Live-Ticker hat genug eigene Regeln fuer eine eigene Datei -- gehoert
// aber zum selben Vertrag und wird deshalb hier mit herausgereicht.
export * from './ticker.ts';
// Dasselbe fuer Hintergrund, Bildkorrektur und Freistellen.
export * from './look.ts';
// Und fuer die Ergebnisquelle -- von Hand oder aus RACE RESULT.
export * from './results.ts';
// Mikrofon, Bildschirmton und Kamera je Quelle plus die Schalter am Ausgang.
export * from './feed.ts';
// Sponsorenliste, Logo im Bild und Werbepause.
export * from './ads.ts';
// Fremde Videosignale per RTMP -- Drohne, Action-Cam, Handy-Bildschirm per App.
export * from './ingest.ts';
// Wetter am Event-Ort.
export * from './weather.ts';
// Video- und Sprachbotschaften an die Regie.
export * from './clips.ts';
// Athleten-Pop-up: Headshot und Netzwerke aus der Startliste.
export * from './spotlight.ts';
import type {
  AdBannerConfig,
  AdFrameKind,
  AdLShapeConfig,
  AdLShapeSchedule,
  AdLiveLayout,
  AdSchedule,
  AdsState,
  LShapeImageStyle,
  MaterialUse,
  SponsorLogoConfig,
} from './ads.ts';
import type { WeatherConfig, WeatherState } from './weather.ts';
import type { CameraLook, StageLook } from './look.ts';
import type { ResultsStatus } from './results.ts';
import type { ParticipantSocials, SpotlightConfig, SpotlightState } from './spotlight.ts';
import type { ClipPolicy, ClipsState, MyClipsState } from './clips.ts';
import type { FeedPatch, OutputPatch, OutputState, OwnFeedPatch, SourceFeed } from './feed.ts';
import type {
  BackyardPatch,
  CornerSlot,
  CourseAction,
  PipLayout,
  PipMode,
  RaceEntryPatch,
  SplitFit,
  SplitLayout,
  TickerBoardPatch,
  TickerEntry,
  TickerEntryKind,
  TickerMode,
  TickerState,
  WidgetId,
  WidgetSize,
  WidgetSlot,
  WidgetState,
} from './ticker.ts';

/* --------------------------------------------------------------- Konten */

export interface PublicUser {
  id: string;
  email: string;
  displayName: string;
}

/** Wie eng jemand mit einem Event verbunden ist. */
export type AccessLevel = 'admin' | 'deputy' | 'director' | null;

export interface EventAccess {
  level: AccessLevel;
  canDirect: boolean;
  /** Nur der Admin darf die Regie-Liste aendern. */
  canManage: boolean;
}

/* ------------------------------------------------------------- Geofence */

/**
 * Bereich, in dem man sich aufhalten muss, um senden zu duerfen.
 * `point`: ein Mittelpunkt, `radiusMeters` ist der Umkreis.
 * `route`: eine Linie, `radiusMeters` ist der halbe Korridor daneben.
 */
export interface Geofence {
  mode: 'point' | 'route';
  /** 'warn' laesst durch und markiert, 'block' weist ab. */
  enforcement: 'warn' | 'block';
  radiusMeters: number;
  /** [lat, lng] -- bei 'point' genau einer, bei 'route' mindestens zwei. */
  points: [number, number][];
}

/**
 * `imprecise` ist der Fall, der ohne GPS entsteht: der Browser liefert dann
 * eine aus WLAN oder IP geschaetzte Position, deren gemeldete Ungenauigkeit
 * bei zig Kilometern liegt. Daraus eine Entfernung zu rechnen ergaebe eine
 * selbstbewusst aussehende, aber falsche Zahl -- also sagen wir es lieber.
 */
export type GeoStatus = 'inside' | 'outside' | 'unknown' | 'imprecise';

/**
 * Ab dieser gemeldeten Ungenauigkeit sagt ein Standort nichts mehr darueber
 * aus, ob jemand in einer Zone von einigen hundert Metern steht. Ein
 * GPS-Fix liegt im Freien bei 5-20 m, ueber WLAN bei einigen hundert -- was
 * darueber liegt, kommt aus der Grobortung.
 */
export const MAX_USABLE_ACCURACY_METERS = 1000;

export interface GeoCheck {
  status: GeoStatus;
  /** Abstand zur Zone in Metern; null, wenn kein Standort vorlag. */
  distanceMeters: number | null;
  /** Genauigkeit der Ortung in Metern, wie vom Browser gemeldet. */
  accuracy: number | null;
}

export interface Coords {
  lat: number;
  lng: number;
  accuracy?: number;
}

export const geoStatusLabel: Record<GeoStatus, string> = {
  inside: 'vor Ort',
  outside: 'außerhalb',
  unknown: 'Standort unbekannt',
  imprecise: 'Standort zu ungenau',
};

export type Role = 'organizer' | 'athlete' | 'spectator';

/* ------------------------------------------------------ Katalog fuer Filter */

export type SportId =
  | 'laufen'
  | 'fussball'
  | 'radsport'
  | 'tennis'
  | 'handball'
  | 'basketball'
  | 'volleyball'
  | 'schwimmen'
  | 'wintersport'
  | 'kampfsport'
  | 'reitsport'
  | 'sonstiges';

export interface SportInfo {
  id: SportId;
  label: string;
  icon: string;
}

export const SPORTS: SportInfo[] = [
  { id: 'laufen', label: 'Laufen', icon: '🏃' },
  { id: 'fussball', label: 'Fußball', icon: '⚽' },
  { id: 'radsport', label: 'Radsport', icon: '🚴' },
  { id: 'tennis', label: 'Tennis', icon: '🎾' },
  { id: 'handball', label: 'Handball', icon: '🤾' },
  { id: 'basketball', label: 'Basketball', icon: '🏀' },
  { id: 'volleyball', label: 'Volleyball', icon: '🏐' },
  { id: 'schwimmen', label: 'Schwimmen', icon: '🏊' },
  { id: 'wintersport', label: 'Wintersport', icon: '⛷️' },
  { id: 'kampfsport', label: 'Kampfsport', icon: '🥋' },
  { id: 'reitsport', label: 'Reitsport', icon: '🏇' },
  { id: 'sonstiges', label: 'Sonstiges', icon: '🏅' },
];

export const sportById = (id: string): SportInfo =>
  SPORTS.find((s) => s.id === id) ?? SPORTS[SPORTS.length - 1];

export interface CountryInfo {
  code: string;
  label: string;
  flag: string;
}

export const COUNTRIES: CountryInfo[] = [
  { code: 'DE', label: 'Deutschland', flag: '🇩🇪' },
  { code: 'AT', label: 'Österreich', flag: '🇦🇹' },
  { code: 'CH', label: 'Schweiz', flag: '🇨🇭' },
  { code: 'NL', label: 'Niederlande', flag: '🇳🇱' },
  { code: 'DK', label: 'Dänemark', flag: '🇩🇰' },
  { code: 'FR', label: 'Frankreich', flag: '🇫🇷' },
  { code: 'IT', label: 'Italien', flag: '🇮🇹' },
  { code: 'PL', label: 'Polen', flag: '🇵🇱' },
  { code: 'ES', label: 'Spanien', flag: '🇪🇸' },
  { code: 'GB', label: 'Großbritannien', flag: '🇬🇧' },
];

export const countryByCode = (code: string): CountryInfo =>
  COUNTRIES.find((c) => c.code === code) ?? { code, label: code, flag: '🏳️' };

export interface LanguageInfo {
  code: string;
  label: string;
}

/** Sprache, in der kommentiert und gechattet wird. */
export const LANGUAGES: LanguageInfo[] = [
  { code: 'de', label: 'Deutsch' },
  { code: 'en', label: 'Englisch' },
  { code: 'nl', label: 'Niederländisch' },
  { code: 'da', label: 'Dänisch' },
  { code: 'fr', label: 'Französisch' },
  { code: 'it', label: 'Italienisch' },
  { code: 'pl', label: 'Polnisch' },
  { code: 'es', label: 'Spanisch' },
];

export const languageByCode = (code: string): LanguageInfo =>
  LANGUAGES.find((l) => l.code === code) ?? { code, label: code.toUpperCase() };
/**
 * Bilder, die an einem Event haengen. `backdrop` ist der Buehnenhintergrund
 * hinter dem Kamerabild -- siehe `BackdropMode` in `look.ts`.
 */
export const EVENT_IMAGE_KINDS = ['cover', 'logo', 'backdrop'] as const;
export type EventImageKind = (typeof EVENT_IMAGE_KINDS)[number];

/** Woher das Bild kommt -- Handykamera oder geteilter Bildschirm. */
export type CaptureKind = 'camera' | 'screen';
export type EventStatus = 'upcoming' | 'live' | 'finished';
export type SourceStatus = 'pending' | 'approved' | 'rejected' | 'ended';

export interface Participant {
  id: string;
  bib: string;
  name: string;
  team?: string;
  category?: string;
  /** Fuers Athleten-Pop-up: ein Satz zur Person, z. B. „Vorjahressiegerin, 38 Loops". */
  info?: string;
  /** Fuers Athleten-Pop-up: Instagram, TikTok & Co. -- siehe `SOCIALS`. */
  socials?: ParticipantSocials;
  /** Wann der Headshot zuletzt kam; ohne Angabe gibt es keinen (`headshotUrl`). */
  photoUpdatedAt?: number;
}

export interface RaceEvent {
  id: string;
  slug: string;
  title: string;
  club: string;
  /** Grobe Kategorie -- danach wird gefiltert. */
  sport: SportId;
  /** Feine Beschreibung, z. B. "Straßenlauf · 10 km". */
  discipline: string;
  description: string;
  location: string;
  country: string;
  language: string;
  startsAt: string;
  status: EventStatus;
  accent: string;
  /** Zeitstempel der letzten Bildaenderung -- dient als Cache-Buster. */
  coverUpdatedAt?: number;
  logoUpdatedAt?: number;
  backdropUpdatedAt?: number;
  /** Notfall-Zugang neben den Konten. Im POC im Klartext. */
  organizerPin: string;
  /** Konto, dem das Event gehoert. Darf als Einziges die Regie-Liste aendern. */
  adminEmail: string;
  /** Zweite verantwortliche Person, immer regieberechtigt. */
  deputyEmail: string | null;
  /** Weitere E-Mails, die in die Regie duerfen (Whitelist). */
  directors: string[];
  geofence: Geofence | null;
  participants: Participant[];
}

export interface EventSummary {
  id: string;
  slug: string;
  title: string;
  club: string;
  sport: SportId;
  discipline: string;
  description: string;
  location: string;
  country: string;
  language: string;
  startsAt: string;
  status: EventStatus;
  accent: string;
  /** Fertige URLs oder null -- die Bilder liegen nicht im JSON. */
  coverUrl: string | null;
  logoUrl: string | null;
  backdropUrl: string | null;
  /** Oeffentlich, damit Sendewillige die Zone sehen. Ohne E-Mail-Adressen. */
  geofence: Geofence | null;
  participantCount: number;
  liveSources: number;
  viewers: number;
}

/** Eine Kamera, die sich beim Event angemeldet hat. */
export interface StreamSource {
  id: string;
  eventId: string;
  /** Verbindungs-ID des Publishers -- Adresse fuer die WebRTC-Signalisierung. */
  connectionId: string;
  displayName: string;
  role: Role;
  bib?: string;
  label: string;
  kind: CaptureKind;
  /** Wo die Quelle beim Anfragen stand -- die Regie sieht das. */
  geo: GeoCheck;
  status: SourceStatus;
  /** true, sobald der Publisher tatsaechlich Medien sendet. */
  live: boolean;
  /**
   * Welche Spuren dieser Quelle gerade rausgehen. Geschaltet wird in der
   * Regie, abgeschaltet auf dem sendenden Geraet -- siehe feed.ts.
   */
  feed: SourceFeed;
  /**
   * Die Verbindung des Publishers ist weg, die Quelle aber noch nicht
   * aufgegeben -- ein Reload dauert ein paar Sekunden. Waehrenddessen bleibt
   * die Quelle im Hauptbild stehen, statt alle auf Schwarz zu schalten.
   */
  reconnecting: boolean;
  /**
   * Die Regie hat „Bereit machen" gedrueckt: gleich geht diese Quelle live.
   * Erlischt von selbst, sobald sie im Bild ist -- siehe `onAirLight`.
   */
  cued: boolean;
  createdAt: number;
  /**
   * Kommt per RTMP-Eingang statt aus einem Browser. Dann gibt es keinen
   * Publisher im Raum: das Bild holt jeder Empfaenger per WHEP beim Server
   * ab, und `connectionId` ist nur eine feste Kennung des Eingangs.
   */
  ingest?: { inputId: string };
}

/**
 * So lange darf eine Quelle verschwunden sein, bevor sie wirklich als beendet
 * gilt. Deckt einen Reload (1-3 s), einen Tabwechsel und einen kurzen
 * Funkloch-Aussetzer ab, ohne dass ein echter Abbruch minutenlang haengt.
 */
export const RECONNECT_GRACE_MS = 25_000;

/**
 * Die Ampel fuer alle, die senden: bin ich live?
 *
 * - `red`: Kamera und Mikro gehen nicht in den Stream.
 * - `yellow`: die Regie hat „Bereit machen" gedrueckt -- gleich geht es los,
 *   noch ist aber nichts im Stream.
 * - `green`: im Hauptbild oder im kleinen Fenster. Man sieht und hoert alles.
 *
 * Gruen haengt allein am Bild, nicht an Mikro- oder Bildschaltern: wer im
 * Bild ist, soll sich so verhalten, als liefe alles -- ein „Mikro aus" ist
 * schneller wieder an, als man es merkt.
 */
export type OnAirLight = 'red' | 'yellow' | 'green';

export const onAirLight = (
  source: Pick<StreamSource, 'id' | 'status' | 'cued'> | null | undefined,
  activeSourceId: string | null,
  overlaySourceId: string | null,
): OnAirLight =>
  !source || source.status !== 'approved'
    ? 'red'
    : source.id === activeSourceId || source.id === overlaySourceId
      ? 'green'
      : source.cued
        ? 'yellow'
        : 'red';

export interface ChatMessage {
  id: string;
  eventId: string;
  authorId: string;
  displayName: string;
  role: Role;
  text: string;
  ts: number;
  system?: boolean;
}

export interface Presence {
  total: number;
  athletes: number;
  spectators: number;
  organizers: number;
}

export interface SelfInfo {
  connectionId: string;
  role: Role;
  displayName: string;
  bib?: string;
}

/**
 * WebRTC-Nutzlast, die der Server nur durchreicht (er liest sie nie).
 *
 * `lane` sagt, in welche Richtung die Nachricht gehoert: 'down' kommt vom
 * Publisher, 'up' vom Zuschauer. Ohne das koennten sich die beiden Rollen in
 * die Quere kommen, wenn dieselbe Person gleichzeitig sendet und schaut.
 */
export type RtcLane = 'up' | 'down';

/**
 * Jedes Signal nennt die Quelle, um die es geht.
 *
 * Die Verbindungs-ID allein reicht nicht mehr: seit ein Tab zwei Quellen
 * senden kann (Kamera *und* geteilter Bildschirm), stehen zwischen denselben
 * zwei Verbindungen zwei PeerConnections. Ohne die Quelle im Signal landete
 * die Antwort auf das Angebot der einen beim Aushandeln der anderen -- und
 * dann kommt bei keiner von beiden ein Bild an.
 */
export type RtcPayload =
  | { lane: RtcLane; sourceId: string; kind: 'offer'; sdp: RTCSessionDescriptionInitLike }
  | { lane: RtcLane; sourceId: string; kind: 'answer'; sdp: RTCSessionDescriptionInitLike }
  | { lane: RtcLane; sourceId: string; kind: 'ice'; candidate: unknown }
  /** Sprechleitung zu: der Sender legt auf. Sonst merkt es das Handy erst Sekunden spaeter. */
  | { lane: RtcLane; sourceId: string; kind: 'bye' };

/**
 * Die Sprechleitung der Regie zu einem Handy (Gegensprechen) laeuft ueber
 * dieselbe Signalisierung wie eine Kamera, nur unter einer Kennung mit
 * diesem Praefix statt einer Quellen-ID: `talk:<Verbindung der Regie>`. Sie
 * ist keine Quelle -- sie steht in keiner Liste und geht nie ins Programm.
 */
export const TALK_PREFIX = 'talk:';

export interface RTCSessionDescriptionInitLike {
  type: 'offer' | 'answer' | 'pranswer' | 'rollback';
  sdp?: string;
}

/* ------------------------------------------------- Ausgang zu YouTube/Twitch */

export type RestreamPlatform = 'youtube' | 'twitch' | 'custom';

export interface RestreamPlatformInfo {
  id: RestreamPlatform;
  label: string;
  /** RTMP-Server der Plattform. Bei 'custom' gibt die Regie ihn selbst an. */
  ingestUrl: string;
  hint: string;
}

export const RESTREAM_PLATFORMS: RestreamPlatformInfo[] = [
  {
    id: 'youtube',
    label: 'YouTube',
    ingestUrl: 'rtmp://a.rtmp.youtube.com/live2',
    hint: 'YouTube Studio → Livestream → Streamschlüssel',
  },
  {
    id: 'twitch',
    label: 'Twitch',
    ingestUrl: 'rtmp://live.twitch.tv/app',
    hint: 'Twitch → Creator-Dashboard → Einstellungen → Stream',
  },
  {
    id: 'custom',
    label: 'Eigener RTMP-Server',
    ingestUrl: '',
    hint: 'Vollstaendige rtmp:// oder rtmps:// Adresse ohne Schluessel',
  },
];

export const restreamPlatform = (id: string): RestreamPlatformInfo =>
  RESTREAM_PLATFORMS.find((p) => p.id === id) ?? RESTREAM_PLATFORMS[RESTREAM_PLATFORMS.length - 1];

/**
 * Ein Ziel, wie es die Regie eingibt. Der Schluessel geht genau einmal zum
 * Server -- zurueck kommt er nie, auch nicht an die Regie selbst.
 */
export interface RestreamTargetInput {
  platform: RestreamPlatform;
  /** Nur bei 'custom' gefuellt. */
  url?: string;
  streamKey: string;
}

/** Oeffentliche Sicht auf ein Ziel: ohne Schluessel, nur mit Erkennungshilfe. */
export interface RestreamTarget {
  id: string;
  platform: RestreamPlatform;
  url: string;
  /** Letzte vier Zeichen des Schluessels -- genug zum Wiedererkennen. */
  keyHint: string;
}

/**
 * `starting` = Renderer und Encoder fahren hoch, `live` = es fliessen Daten,
 * `retrying` = etwas ist weggebrochen und wird gerade neu aufgebaut.
 */
export type RestreamPhase = 'idle' | 'starting' | 'live' | 'retrying' | 'error';

export const restreamPhaseLabel: Record<RestreamPhase, string> = {
  idle: 'aus',
  starting: 'startet',
  live: 'sendet',
  retrying: 'baut neu auf',
  error: 'Fehler',
};

export interface RestreamStatus {
  phase: RestreamPhase;
  targets: RestreamTarget[];
  /** Beginn der laufenden Sendung; ueberlebt jeden Reload der Regie. */
  startedAt: number | null;
  /** Wie oft sich der Job seit dem Start selbst neu aufgebaut hat. */
  restarts: number;
  /** Bisher an den Encoder uebergebene Bytes -- grober Fortschrittsbeleg. */
  bytesSent: number;
  message: string | null;
  /** Wer gestartet hat, damit eine zweite Regie es einordnen kann. */
  startedBy: string | null;
}

export const idleRestream = (): RestreamStatus => ({
  phase: 'idle',
  targets: [],
  startedAt: null,
  restarts: 0,
  bytesSent: 0,
  message: null,
  startedBy: null,
});

/* ---------------------------------------------------------------- Client -> Server */

export type ClientMessage =
  | {
      t: 'hello';
      eventId: string;
      role: Role;
      displayName: string;
      bib?: string;
      /**
       * Kennung des Browser-Tabs (sessionStorage): ueberlebt einen Reload,
       * gilt aber nicht fuer einen zweiten Tab. Die connectionId wechselt bei
       * jedem Reload -- ohne diese Kennung koennte eine zurueckkehrende Seite
       * ihre laufende Quelle nicht wieder uebernehmen, und ein zweiter Tab
       * derselben Person wuerde sie faelschlich an sich reiszen.
       */
      deviceId?: string;
      /** Sitzungstoken eines angemeldeten Kontos -- der regulaere Regie-Weg. */
      authToken?: string;
      /** Notfall-Zugang, falls kein Konto zur Hand ist. */
      organizerPin?: string;
      /**
       * Kennung dieses Browsers (localStorage), anders als `deviceId` fuer alle
       * Tabs gleich. Daran haengen die Botschaften eines Absenders und seine
       * Grenze -- ein zweiter Tab ist kein zweiter Absender.
       */
      clientId?: string;
    }
  | { t: 'chat:send'; text: string }
  | { t: 'stream:request'; label: string; kind?: CaptureKind; location?: Coords | null }
  /**
   * Medien fliessen. `sourceId` ist noetig, seit ein Tab zwei Quellen halten
   * kann (Kamera *und* geteilter Bildschirm) -- ohne sie waere nicht klar,
   * welche der beiden gemeint ist. Fehlt sie, gilt die juengste.
   */
  | { t: 'stream:live'; live: boolean; sourceId?: string }
  /** Ohne `sourceId` endet alles, was dieser Tab sendet. */
  | { t: 'stream:cancel'; sourceId?: string }
  /** Der Sender schaltet sein eigenes Mikro oder Bild -- unabhaengig von der Regie. */
  | { t: 'source:own'; sourceId: string; patch: OwnFeedPatch }
  /** Ampel auf Gelb: „mach dich bereit" an diese Quelle -- oder zurueck. */
  | { t: 'source:cue'; sourceId: string; on: boolean }
  | { t: 'stream:decide'; sourceId: string; decision: 'approve' | 'reject' }
  | { t: 'stream:drop'; sourceId: string }
  | { t: 'program:set'; sourceId: string | null }
  /** Zweite Quelle als kleines Fenster ueber dem Hauptbild -- oder daneben im Splitscreen. */
  | { t: 'overlay:set'; sourceId: string | null }
  /** Wo und wie gross die zweite Quelle steht: Ecke und Groesse oder Aufteilung im Splitscreen. */
  | {
      t: 'overlay:layout';
      slot?: CornerSlot;
      size?: WidgetSize;
      mode?: PipMode;
      split?: SplitLayout;
      swap?: boolean;
      fit?: SplitFit;
    }
  /* -- Live-Ticker: alles nur fuer die Regie ---------------------------- */
  | { t: 'ticker:mode'; mode: TickerMode }
  /** Namen, Kuerzel, Abschnitt, Notiz, Aufschlag -- und Korrekturen am Stand. */
  | { t: 'ticker:board'; patch: TickerBoardPatch }
  /** `set` stellt die Uhr, `target` die geplante Dauer (0 loescht sie wieder). */
  | { t: 'ticker:clock'; action: 'start' | 'stop' | 'reset' | 'set' | 'target'; seconds?: number }
  /** Tor bzw. Zaehler: `delta` ist +1 oder -1. */
  | { t: 'ticker:score'; side: 0 | 1; delta: number; text?: string }
  /** Tennis: ein Punkt fuer eine Seite, den Rest rechnet der Server. */
  | { t: 'ticker:point'; side: 0 | 1 }
  /** Backyard: Rundenlaenge stellen, Runden und Feld hoch- und runterzaehlen. */
  | { t: 'ticker:backyard'; patch: BackyardPatch }
  /** Mikrofon, Bildschirmton oder Bild einer Quelle schalten. */
  | { t: 'source:feed'; sourceId: string; patch: FeedPatch }
  /** Master-Stumm und Master-Abblende fuer den ganzen Ausgang. */
  | { t: 'output:set'; patch: OutputPatch }
  /** Person auf der Strecke anlegen oder aendern. */
  | { t: 'ticker:entry'; entry: RaceEntryPatch }
  | { t: 'ticker:entry:remove'; entryId: string }
  /** Backyard-Streckenkarte: zurueck am Start, oder gerade an dieser Stelle der Runde. */
  | { t: 'ticker:course'; entryId: string; action: CourseAction; fraction?: number }
  /** Freier Eintrag im Ticker-Feed. */
  | { t: 'ticker:post'; kind: TickerEntryKind; text: string; mark?: string }
  | { t: 'ticker:unpost'; entryId: string }
  /** Einblendungen ueber dem Hauptbild schalten. */
  /** `color: null` setzt die Einblendung auf das Standard-Rot zurueck. */
  | {
      t: 'widget:set';
      widget: WidgetId;
      on?: boolean;
      slot?: WidgetSlot;
      size?: WidgetSize;
      color?: string | null;
      /** Nur Strecke: Vollbild oder Kaestchen. */
      full?: boolean;
    }
  /* -- Aussehen des Hauptbilds: Hintergrund und Bild pro Kamera ---------- */
  | { t: 'look:stage'; patch: Partial<StageLook> }
  | { t: 'look:camera'; sourceId: string; patch: Partial<CameraLook> }
  /* -- Werbung: Logo im Bild und Werbepause ----------------------------- */
  | { t: 'ads:logo'; patch: Partial<SponsorLogoConfig> }
  /** Wo das Live-Bild waehrend der Werbepause klein weiterlaeuft. */
  | { t: 'ads:live'; patch: Partial<AdLiveLayout> }
  /**
   * Name, Werbetext, Farbe, Hinweis und QR-Link eines Sponsors; Bilder und
   * Videos gehen per REST hoch. `color: null` nimmt die Farbe wieder aus dem Logo.
   */
  | {
      t: 'ads:sponsor';
      sponsorId: string;
      name?: string;
      tagline?: string;
      color?: string | null;
      cta?: string;
      link?: string;
    }
  /** Wofuer ein Stueck Material gedacht ist -- und ob ein Bild in der L-Form Eckbild oder L-Grafik ist. */
  | { t: 'ads:material'; sponsorId: string; materialId: string; uses?: MaterialUse[]; lshapeStyle?: LShapeImageStyle }
  /** Banner: oben oder unten, Laufband oder Bannerbilder, Hoehe, Wechseltakt. */
  | { t: 'ads:banner'; patch: Partial<AdBannerConfig> }
  /** L-Form: in welcher Ecke sich die Streifen treffen und wie breit sie sind. */
  | { t: 'ads:lshape'; patch: Partial<AdLShapeConfig> }
  /**
   * Banner oder L-Form einblenden -- `null` blendet aus. `durationMs: null`
   * heisst: bis zum Ausblenden. `sponsorIds`: nur deren Material laeuft in
   * der L-Form; fehlt es oder ist leer, laeuft alles.
   */
  | { t: 'ads:frame'; kind: AdFrameKind | null; durationMs?: number | null; sponsorIds?: string[] }
  /** Laufende L-Form: naechstes Stueck sofort, Stueck halten oder wieder weiterlaufen lassen. */
  | { t: 'ads:lshapeRun'; action: 'next' | 'hold' | 'release' }
  /** L-Form nach Zeitplan: Modus, Abstand und wie lange sie steht. */
  | { t: 'ads:lshapeSchedule'; patch: Partial<AdLShapeSchedule> }
  /** Antwort auf die faellige L-Form: jetzt, fuenf Minuten spaeter oder diesmal nicht. */
  | { t: 'ads:lshapeDue'; action: 'now' | 'snooze' | 'skip' }
  /** Werbepause mit diesem Video starten -- `null` bricht sie ab. */
  | { t: 'ads:break'; materialId: string | null }
  /** Werbung nach Zeitplan: Modus und Abstand. */
  | { t: 'ads:schedule'; patch: Partial<AdSchedule> }
  /** Wetter: Ansicht der Einblendung und ein anderer Ort -- `place: null` nimmt wieder den des Events. */
  | { t: 'weather:set'; patch: Partial<WeatherConfig> }
  /** Antwort auf die faellige Pause: jetzt, fuenf Minuten spaeter oder diesmal nicht. */
  | { t: 'ads:due'; action: 'now' | 'snooze' | 'skip' }
  /**
   * Botschaften: freigeben, als Favorit markieren, in den Papierkorb legen
   * oder daraus zurueckholen. Hochgeladen wird per REST.
   */
  | { t: 'clip:decide'; clipId: string; action: 'approve' | 'favorite' | 'unfavorite' | 'trash' | 'restore' }
  /** Botschaft im Stream spielen -- `null` bricht sie ab. */
  | { t: 'clip:air'; clipId: string | null }
  /** Absender blockieren, wieder zulassen oder ihm weitere Botschaften erlauben. */
  | { t: 'clip:sender'; senderKey: string; action: 'block' | 'unblock' | 'more' }
  /** Absender an der Grenze bittet die Regie um weitere Botschaften. */
  | { t: 'clip:ask' }
  /** Von wem die Regie Botschaften annimmt: alle, nur vor Ort, niemand. */
  | { t: 'clip:policy'; policy: ClipPolicy }
  /** Athleten-Pop-up zur Startnummer zeigen -- fuenf Sekunden, dann von selbst weg. */
  | { t: 'spotlight:show'; bib: string }
  /** Laufendes Pop-up vorzeitig wegnehmen. */
  | { t: 'spotlight:hide' }
  /** Ecke, Groesse, Farbe des Pop-ups -- `color: null` heisst zurueck auf Rot. */
  | { t: 'spotlight:config'; patch: Partial<Omit<SpotlightConfig, 'color'>> & { color?: string | null } }
  /**
   * Durchgang: die Pop-ups nacheinander -- wer drankommt und in welchem
   * Abstand, steht in der Einstellung (`tourWho`, `tourEmpty`, `tourGap`).
   */
  | { t: 'spotlight:tour'; action: 'start' | 'skip' | 'stop' }
  | { t: 'subscribe'; sourceId: string }
  | { t: 'unsubscribe'; sourceId: string }
  | { t: 'rtc:signal'; to: string; data: RtcPayload }
  | { t: 'ping' };

/* ---------------------------------------------------------------- Server -> Client */

export type ServerMessage =
  | {
      t: 'welcome';
      self: SelfInfo;
      event: EventSummary;
      sources: StreamSource[];
      activeSourceId: string | null;
      overlaySourceId: string | null;
      chat: ChatMessage[];
      presence: Presence;
      pip: PipLayout;
      ticker: TickerState;
      tickerFeed: TickerEntry[];
      widgets: WidgetState;
      stage: StageLook;
      cameras: Record<string, CameraLook>;
      restream: RestreamStatus;
      results: ResultsStatus;
      output: OutputState;
      ads: AdsState;
      weather: WeatherState;
      /** Alle Botschaften -- nur fuer die Regie, sonst null. */
      clips: ClipsState | null;
      /** Die eigenen Botschaften dieses Absenders und seine Grenze. */
      myClips: MyClipsState;
      /** Athleten-Pop-up: das laufende und die Einstellung. */
      spotlight: SpotlightState;
      iceServers: RTCIceServerLike[];
      instanceId: string;
    }
  | { t: 'sources'; sources: StreamSource[] }
  /** Ticker-Zustand und -Feed gehen immer zusammen raus -- beide sind klein. */
  | { t: 'ticker'; ticker: TickerState; feed: TickerEntry[] }
  | { t: 'widgets'; widgets: WidgetState }
  /** Hintergrund und Bildkorrektur je Kamera -- gehen zusammen raus. */
  | { t: 'look'; stage: StageLook; cameras: Record<string, CameraLook> }
  /** Zustand des RTMP-Ausgangs -- liegt auf dem Server, nicht im Regie-Tab. */
  | { t: 'restream'; status: RestreamStatus }
  /** Zustand der Ergebnisquelle -- laeuft aus demselben Grund oben. */
  | { t: 'results'; status: ResultsStatus }
  /** Ton und Bild am Ausgang -- der Master-Griff der Regie. */
  | { t: 'output'; output: OutputState }
  /** Sponsoren, Logo im Bild und eine laufende Werbepause. */
  | { t: 'ads'; ads: AdsState }
  /** Wetter-Einstellung und die zuletzt geholte Vorhersage. */
  | { t: 'weather'; weather: WeatherState }
  /** Botschaften und Absender -- geht nur an die Regie. */
  | { t: 'clips'; clips: ClipsState }
  /** Die eigenen Botschaften -- geht nur an die Verbindungen dieses Absenders. */
  | { t: 'clips:mine'; mine: MyClipsState }
  /** Athleten-Pop-up: kommt, geht, oder die Einstellung hat sich geaendert. */
  | { t: 'spotlight'; spotlight: SpotlightState }
  | { t: 'program'; activeSourceId: string | null; overlaySourceId: string | null; pip: PipLayout }
  | { t: 'chat'; message: ChatMessage }
  | { t: 'presence'; presence: Presence }
  | { t: 'peer:subscribe'; subscriberId: string; sourceId: string }
  | { t: 'peer:unsubscribe'; subscriberId: string; sourceId: string }
  /**
   * `fromRole` setzt der Server, nicht der Absender. Daran erkennt ein Handy,
   * dass eine Sprechleitung wirklich von der Regie kommt -- weitergereicht
   * wird sonst zwischen beliebigen Verbindungen eines Events.
   */
  | { t: 'rtc:signal'; from: string; fromRole?: Role; data: RtcPayload }
  | { t: 'error'; code: string; message: string }
  | { t: 'pong' };

export interface RTCIceServerLike {
  urls: string | string[];
  username?: string;
  credential?: string;
}

export const imageUrl = (slug: string, kind: EventImageKind, version?: number) =>
  version ? `/api/events/${slug}/${kind}?v=${version}` : null;

export function toSummary(event: RaceEvent, liveSources: number, viewers: number): EventSummary {
  // E-Mail-Adressen und PIN duerfen die oeffentliche Antwort nie verlassen.
  const {
    participants,
    organizerPin,
    adminEmail,
    deputyEmail,
    directors,
    coverUpdatedAt,
    logoUpdatedAt,
    backdropUpdatedAt,
    ...rest
  } = event;
  void organizerPin;
  void adminEmail;
  void deputyEmail;
  void directors;
  return {
    ...rest,
    coverUrl: imageUrl(event.slug, 'cover', coverUpdatedAt),
    logoUrl: imageUrl(event.slug, 'logo', logoUpdatedAt),
    backdropUrl: imageUrl(event.slug, 'backdrop', backdropUpdatedAt),
    participantCount: participants.length,
    liveSources,
    viewers,
  };
}
